import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Users, RotateCcw, Download, Search, Info, Power, X, Loader2, ChevronLeft, ChevronRight,
  Rocket, RefreshCw, UserPen, Send, Eye, LogIn, CircleDot,
} from "lucide-react";

const ACTIVITY_STYLE = {
  plan_activated: { color: "bg-emerald-500", icon: Rocket },
  plan_changed: { color: "bg-amber-500", icon: RefreshCw },
  profile_updated: { color: "bg-sky-500", icon: UserPen },
  application_sent: { color: "bg-indigo-500", icon: Send },
  plan_viewed: { color: "bg-violet-500", icon: Eye },
  last_login: { color: "bg-slate-400", icon: LogIn },
};

const PLAN_BADGE = {
  premium: "bg-indigo-50 text-indigo-700",
  standard: "bg-sky-50 text-sky-700",
  free: "bg-slate-100 text-slate-600",
};

function fmtDate(iso, withTime = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return withTime ? `${date} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : date;
}

function initials(name, email) {
  const s = (name && name !== "—" ? name : email || "?").trim();
  return s[0]?.toUpperCase() || "?";
}

function CandidateDrawer({ id, onClose, onStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/candidates/${id}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => toast.error("Could not load candidate details."))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} data-testid="drawer-overlay" />
      <div data-testid="candidate-drawer"
        className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-slate-900 font-display">Candidate Details</h2>
          <button data-testid="drawer-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-amber-500" /></div>
        ) : (
          <div className="px-6 py-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-slate-900 text-amber-400 flex items-center justify-center font-bold text-lg">
                {initials(data.name, data.email)}
              </div>
              <div>
                <div className="font-bold text-slate-900 text-lg">{data.name}</div>
                <div className="text-slate-500 text-sm">{data.email}</div>
              </div>
            </div>

            <dl className="mt-6 space-y-3.5">
              <Row label="Current Plan">
                <span className={`px-2.5 py-0.5 rounded-md text-sm font-semibold ${PLAN_BADGE[data.plan_slug] || PLAN_BADGE.free}`}>{data.plan} Plan</span>
              </Row>
              <Row label="Status">
                <span className={`px-2.5 py-0.5 rounded-md text-sm font-semibold ${data.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                  {data.status === "active" ? "Active" : "Inactive"}
                </span>
              </Row>
              <Row label="Plan Start Date"><span className="text-slate-800 font-medium">{fmtDate(data.plan_start)}</span></Row>
              <Row label="Plan Expiry Date"><span className="text-slate-800 font-medium">{data.plan_expiry ? fmtDate(data.plan_expiry) : "Lifetime"}</span></Row>
              <Row label="Last Login"><span className="text-slate-800 font-medium">{fmtDate(data.last_login, true)}</span></Row>
            </dl>

            <h3 className="mt-8 text-lg font-bold text-slate-900 font-display">Activity History</h3>
            {data.timeline.length === 0 ? (
              <p className="mt-3 text-sm text-slate-400">No recorded activity yet.</p>
            ) : (
              <ol className="mt-4 relative border-l-2 border-slate-100 ml-1.5 space-y-6" data-testid="activity-timeline">
                {data.timeline.map((t, i) => {
                  const st = ACTIVITY_STYLE[t.type] || { color: "bg-slate-400", icon: CircleDot };
                  const Icon = st.icon;
                  return (
                    <li key={i} className="ml-5 relative">
                      <span className={`absolute -left-[27px] top-0.5 w-4 h-4 rounded-full ${st.color} ring-4 ring-white flex items-center justify-center`}>
                        <Icon className="w-2.5 h-2.5 text-white" />
                      </span>
                      <div className="flex justify-between gap-3">
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{t.title}</div>
                          {t.detail && <div className="text-slate-500 text-sm">{t.detail}</div>}
                        </div>
                        <div className="text-right text-xs text-slate-400 whitespace-nowrap">
                          <div>{fmtDate(t.at)}</div>
                          <div>{new Date(t.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}

            <div className="mt-8 pt-4 border-t border-slate-100">
              <Button data-testid="drawer-toggle-status"
                onClick={() => onStatusChange(data.id, data.status !== "active")}
                variant="outline"
                className={`w-full font-semibold ${data.status === "active" ? "border-rose-300 text-rose-600 hover:bg-rose-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                <Power className="w-4 h-4 mr-2" /> {data.status === "active" ? "Deactivate Candidate" : "Activate Candidate"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-500 text-sm">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default function UserManagement() {
  const [data, setData] = useState({ items: [], total: 0, stats: {}, page: 1, page_size: 10 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");
  const [created, setCreated] = useState("");
  const [page, setPage] = useState(1);
  const [drawerId, setDrawerId] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/admin/plans").then(({ data }) => setPlans(data.items)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 10 };
      if (search) params.search = search;
      if (status) params.status = status;
      if (plan) params.plan = plan;
      if (created) params.created = created;
      const { data } = await api.get("/admin/candidates", { params });
      setData(data);
    } catch {
      toast.error("Could not load candidates.");
    } finally {
      setLoading(false);
    }
  }, [page, search, status, plan, created]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, plan, created]);

  const reset = () => { setSearch(""); setStatus(""); setPlan(""); setCreated(""); setPage(1); };

  const changeStatus = async (id, isActive) => {
    try {
      await api.patch(`/admin/candidates/${id}/status`, { is_active: isActive });
      toast.success(isActive ? "Candidate activated." : "Candidate deactivated.");
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "Plan", "Plan Start", "Status", "Created", "Last Login"]];
    data.items.forEach((c) => rows.push([c.name, c.email, c.phone, c.plan, fmtDate(c.plan_start), c.status, fmtDate(c.created_at), fmtDate(c.last_login, true)]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "candidates.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const stats = data.stats || {};
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / 10));

  return (
    <div data-testid="user-management-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-display text-slate-900">User Management</h1>
        <p className="text-slate-500 mt-1.5">Manage candidate users and their profiles.</p>
      </div>

      {/* Stats */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-md">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Users className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <div className="text-slate-500 text-sm">Candidates</div>
            <div className="text-3xl font-bold text-slate-900 font-display" data-testid="stat-total">{(stats.total || 0).toLocaleString("en-IN")}</div>
            <div className="text-slate-500 text-xs mt-0.5">Active: {stats.active || 0} · Inactive: {stats.inactive || 0}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or phone..."
                className="w-full h-11 rounded-lg border border-slate-300 pl-10 pr-3 text-sm outline-none focus:border-amber-500" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Status</label>
            <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1.5 block h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500 min-w-[140px]">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Plan</label>
            <select data-testid="filter-plan" value={plan} onChange={(e) => setPlan(e.target.value)}
              className="mt-1.5 block h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500 min-w-[140px]">
              <option value="">All Plans</option>
              {plans.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Created</label>
            <input data-testid="filter-created" type="date" value={created} onChange={(e) => setCreated(e.target.value)}
              className="mt-1.5 block h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
          </div>
          <Button data-testid="reset-filters" variant="outline" onClick={reset} className="h-11 border-slate-300 text-slate-600 hover:bg-slate-50">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <Button data-testid="export-btn" variant="outline" onClick={exportCsv} className="h-11 border-slate-300 text-slate-600 hover:bg-slate-50">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-100">
                <th className="px-6 py-3">Name</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Phone</th>
                <th className="px-3 py-3">Plan</th>
                <th className="px-3 py-3">Plan Start</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Created</th>
                <th className="px-3 py-3">Last Login</th>
                <th className="px-3 py-3 pr-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-6 py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={9} className="px-6 py-16 text-center text-slate-400">No candidates found.</td></tr>
              ) : data.items.map((c) => (
                <tr key={c.id} data-testid={`candidate-row-${c.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-slate-900 text-amber-400 flex items-center justify-center font-bold text-xs">{initials(c.name, c.email)}</div>
                      <span className="font-semibold text-slate-900">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3.5 text-slate-500">{c.email}</td>
                  <td className="px-3 py-3.5 text-slate-500">{c.phone}</td>
                  <td className="px-3 py-3.5">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${PLAN_BADGE[c.plan_slug] || PLAN_BADGE.free}`}>{c.plan} Plan</span>
                    {c.plan_expiry && <div className="text-[11px] text-slate-400 mt-1">exp {fmtDate(c.plan_expiry)}</div>}
                  </td>
                  <td className="px-3 py-3.5 text-slate-600">{fmtDate(c.plan_start)}</td>
                  <td className="px-3 py-3.5">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${c.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                      {c.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-3.5 text-slate-600">{fmtDate(c.created_at)}</td>
                  <td className="px-3 py-3.5 text-slate-600">{fmtDate(c.last_login, true)}</td>
                  <td className="px-3 py-3.5 pr-6">
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`info-${c.id}`} onClick={() => setDrawerId(c.id)} title="View details"
                        className="p-1.5 rounded-lg text-sky-600 hover:bg-sky-50">
                        <Info className="w-4 h-4" />
                      </button>
                      <button data-testid={`toggle-${c.id}`} onClick={() => changeStatus(c.id, c.status !== "active")}
                        title={c.status === "active" ? "Deactivate" : "Activate"}
                        className={`p-1.5 rounded-lg ${c.status === "active" ? "text-rose-500 hover:bg-rose-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
                        <Power className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 text-sm text-slate-500">
          <span>Showing {data.items.length ? (page - 1) * 10 + 1 : 0}–{(page - 1) * 10 + data.items.length} of {data.total} candidates</span>
          <div className="flex items-center gap-2">
            <button data-testid="prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-medium text-slate-700">{page} / {totalPages}</span>
            <button data-testid="next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {drawerId && (
        <CandidateDrawer id={drawerId} onClose={() => setDrawerId(null)}
          onStatusChange={(id, active) => { changeStatus(id, active); setDrawerId(null); }} />
      )}
    </div>
  );
}
